import {StandardDialogFooter} from "../../../../pages/components/dialogs/standard-dialog/standard-dialog";
import React, {useState} from "react";
import {DialogContent, Grid} from "@mui/material";
import {BarekamSelect} from "../../../../pages/components/select/select";
import {BarekamMenuItem, SelectedBarekamMenuItem} from "../../../../pages/components/select/styled/orders";
import BarekamInput from "../../../../pages/components/input";
import {useAppDispatch, useAppSelector} from "../../../../redux/store";
import {Message, MessageSections} from "../../../../pages/components/message/message";
import {WarehouseModel} from "../../../../pages/shared/models/order/warehouse.model";
import {AxiosError} from "axios";
import {mapValidationErrors} from "../../../../pages/user/settings/personal-info/personal-info.service";
import BarekamPhoneInput from "../../../../pages/auth/phone-input/phone-input";
import {CountryModel} from "../../../../pages/shared/models/countryModel";
import warehousesService from "../../../../services/warehouses.service";
import {getCountries} from "../../../../redux/slices/static-data.slice";
import {getMessage} from "../../../../pages/shared/helper-functions";

const NewWarehouse = ({onCancel}: NewWarehouseDialogProps) => {
    const [warehouse, setWarehouse] = useState({
        addressLine1: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
        name: ''
    } as WarehouseModel)
    const [warehouseCountry, setWarehouseCountry] = useState<CountryModel>()

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const {countries} = useAppSelector(state => state.staticDataReducer);

    const {messageCache, languageId} = useAppSelector(state => state.messagesReducer);

    const dispatch = useAppDispatch();

    function onChange(field: 'name' | 'country' | 'addressLine1' | 'city' | 'state' | 'zipCode' | 'phone', value: any) {
        if (field === 'country') {
            setWarehouseCountry(countries.find(country => country.countryId === value))
        } else {
            setWarehouse(prevState => {
                prevState[field] = value;
                return {...prevState};
            })
        }

    }

    function validateWarehouse() {
        const errors = {} as {[key: string]: string};

        if (!warehouseCountry) {
            errors['country'] = 'required';
        }
        if (!warehouse.city) {
            errors['city'] = 'required';
        }
        if (!warehouse.addressLine1) {
            errors['addressLine1'] = 'required';
        }
        if (!warehouse.phone) {
            errors['phone'] = 'required';
        }
        if (!warehouse.zipCode) {
            errors['zipCode'] = 'required';
        }

        return errors;
    }

    function createWarehouse() {
        const errors = validateWarehouse();
        setErrors(errors);

        if (Object.keys(errors).length === 0 && warehouse && warehouseCountry) {
            warehousesService.createWarehouse(warehouseCountry, warehouse)
                .then((response: any) => {
                    if (response) {
                        dispatch(getCountries(true))
                        onCancel();
                    }
                })
                .catch(e => {
                    if (e instanceof AxiosError) {
                        setErrors(mapValidationErrors(e));
                    }
                });
        }
    }

    return (
        <>
            <DialogContent>
                <Grid container rowSpacing={'1rem'} columnSpacing={'1rem'}>
                    <Grid item xs={12}>
                        <BarekamInput key={'name'}
                                      customInputVariant={"standard"}
                                      placeholder={getMessage('name', MessageSections.MAIN, messageCache, languageId)}
                                      value={warehouse.name}
                                      onChange={(event) => onChange('name', event.target.value)}
                                      error={!!errors['name']}
                                      errorMessage={errors['name'] && getMessage(errors['name'], MessageSections.MAIN, messageCache, languageId)}
                                      width={'100%'}/>
                    </Grid>

                    <Grid item xs={12}>
                        <BarekamSelect
                            variant={'outlined'}
                            label={getMessage('country', MessageSections.MAIN, messageCache, languageId)}
                            value={warehouseCountry?.countryId || ''}
                            onChange={(value) => onChange('country', value.target.value)}
                            type={"standard"}
                            error={!!errors['country']}
                            errorMessage={errors['country'] && getMessage(errors['country'], MessageSections.MAIN, messageCache, languageId)}>
                            {countries.map((country, index) => {
                                if (country.countryId === warehouseCountry?.countryId) {
                                    return (
                                        <SelectedBarekamMenuItem value={country.countryId}
                                                                 key={index}>
                                            {country.name[languageId]}
                                        </SelectedBarekamMenuItem>
                                    )
                                } else {
                                    return (
                                        <BarekamMenuItem value={country.countryId}
                                                         key={index}>
                                            {country.name[languageId]}
                                        </BarekamMenuItem>
                                    );
                                }
                            })}
                        </BarekamSelect>
                    </Grid>

                    <Grid item xs={12}>
                        <BarekamInput key={'city'}
                                      customInputVariant={"standard"}
                                      label={getMessage('city', MessageSections.MAIN, messageCache, languageId)}
                                      value={warehouse.city}
                                      onChange={(event) => onChange('city', event.target.value)}
                                      error={!!errors['city']}
                                      errorMessage={errors['city'] && getMessage(errors['city'], MessageSections.MAIN, messageCache, languageId)}
                                      width={'100%'}/>
                    </Grid>

                    <Grid item xs={12}>
                        <BarekamInput key={'addressLine1'}
                                      customInputVariant={"standard"}
                                      placeholder={getMessage('addressLine1', MessageSections.MAIN, messageCache, languageId)}
                                      value={warehouse.addressLine1}
                                      onChange={(event) => onChange('addressLine1', event.target.value)}
                                      error={!!errors['addressLine1']}
                                      errorMessage={errors['addressLine1'] && getMessage(errors['addressLine1'], MessageSections.MAIN, messageCache, languageId)}
                                      width={'100%'}/>
                    </Grid>

                    <Grid item xs={12}>
                        <BarekamInput key={'zipCode'}
                                      customInputVariant={"standard"}
                                      placeholder={getMessage('zip_code', MessageSections.MAIN, messageCache, languageId)}
                                      value={warehouse.zipCode}
                                      onChange={(event) => onChange('zipCode', event.target.value)}
                                      error={!!errors['zipCode']}
                                      errorMessage={errors['zip_code'] && getMessage(errors['zip_code'], MessageSections.MAIN, messageCache, languageId)}
                                      width={'100%'}/>
                    </Grid>

                    <Grid item xs={12}>
                        <BarekamPhoneInput key={'register-phone-number'}
                                           value={warehouse.phone}
                                           placeholder={getMessage('phone_number', MessageSections.MAIN, messageCache, languageId)}
                                           onChange={(value: number) => onChange('phone', value)}
                                           error={!!errors['phone']}
                                           errorMessage={errors['phone'] && getMessage(errors['phone'], MessageSections.MAIN, messageCache, languageId)}
                                           width={'100%'}/>
                    </Grid>
                </Grid>
            </DialogContent>

            <StandardDialogFooter onCancel={() => onCancel()}
                                  onSave={() => createWarehouse()}
                                  saveLabel={<Message messageKey={'create'} sectionId={MessageSections.ORDERS}/>}/>
        </>
    );
}

interface NewWarehouseDialogProps {
    onCancel: () => void,
}

export default NewWarehouse;